import { useState } from 'react';
import type { Item, Confidence } from '@mas-flow/types';
import { useCan } from '../auth/AuthProvider';
import { useCastVote } from '../hooks/useVote';
import { useToast } from '../hooks/useToast';
import { ApiHttpError } from '../api/client';

const OPTIONS: Confidence[] = ['High', 'Medium', 'Low'];

export function InlineConfidenceCell({ item }: { item: Item }) {
  const canEdit = useCan('item.score', item);
  const [editing, setEditing] = useState(false);
  const cast = useCastVote();
  const toast = useToast();

  if (!canEdit) {
    return <span className="text-slate-700">{item.confidence ?? '—'}</span>;
  }
  if (!editing) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className="rounded px-1 text-slate-700 hover:bg-slate-100"
      >
        {item.confidence ?? '—'}
      </button>
    );
  }

  const submit = (c: Confidence) => {
    setEditing(false);
    cast.mutate(
      { itemId: item.id, partial: { confidence: c } },
      {
        onError: (err) => {
          if (err instanceof ApiHttpError && err.status === 403) toast('You no longer have permission');
          else toast('Vote failed');
        },
      },
    );
  };

  return (
    <div
      className="flex items-center gap-0.5 rounded border border-brand-tint bg-brand-wash p-0.5"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
    >
      {OPTIONS.map((level) => (
        <button
          key={level}
          type="button"
          aria-label={`Vote confidence as ${level}`}
          onClick={() => submit(level)}
          className={`rounded px-1.5 py-0.5 text-xs ${item.confidence === level ? 'bg-brand text-white' : 'bg-white text-slate-700 hover:bg-brand-wash'}`}
        >
          {level[0]}
        </button>
      ))}
    </div>
  );
}
